"use client";

import { type FormEventHandler, type ReactNode, useId } from "react";
import { cn } from "../../lib";

export interface GlowInputCtaProps {
	/** Visible label text; rendered with `sr-only` (override via `labelClassName`). */
	label: string;
	placeholder: string;
	submitLabel: ReactNode;
	name?: string;
	autoComplete?: string;
	required?: boolean;
	className?: string;
	labelClassName?: string;
	inputClassName?: string;
	submitClassName?: string;
	onSubmit?: FormEventHandler<HTMLFormElement>;
}

/**
 * Dark “glow ring” email row (Tailwind Plus / Commit-style), for newsletter or CTA capture.
 */
export function GlowInputCta({
	label,
	placeholder,
	submitLabel,
	name = "email",
	autoComplete = "email",
	required = true,
	className,
	labelClassName,
	inputClassName,
	submitClassName,
	onSubmit,
}: GlowInputCtaProps) {
	const id = useId();

	return (
		<form
			className={cn(
				"relative isolate mt-8 flex items-center pr-1",
				className,
			)}
			onSubmit={
				onSubmit ??
				((e) => {
					e.preventDefault();
				})
			}
		>
			<label htmlFor={id} className={cn("sr-only", labelClassName)}>
				{label}
			</label>
			<input
				required={required}
				type="email"
				autoComplete={autoComplete}
				name={name}
				id={id}
				placeholder={placeholder}
				className={cn(
					"peer w-0 flex-auto bg-transparent px-4 py-2.5 text-base text-white placeholder:text-gray-500 focus:outline-hidden sm:text-[0.8125rem]/6",
					inputClassName,
				)}
			/>
			<button
				type="submit"
				className={cn(
					"group relative isolate flex-none rounded-md py-1.5 pr-[calc(9/16*1rem)] pl-2.5 text-[0.8125rem]/6 font-semibold text-white",
					submitClassName,
				)}
			>
				<span className="absolute inset-0 rounded-md bg-linear-to-b from-white/80 to-white opacity-10 transition-opacity group-hover:opacity-15" />
				<span className="absolute inset-0 rounded-md opacity-[0.075] shadow-[inset_0_1px_1px_white] transition-opacity group-hover:opacity-10" />
				<span className="relative">
					{submitLabel}{" "}
					<span aria-hidden="true" className="relative">
						→
					</span>
				</span>
			</button>
			<div className="absolute inset-0 -z-10 rounded-lg transition peer-focus:ring-4 peer-focus:ring-sky-300/15" />
			<div className="absolute inset-0 -z-10 rounded-lg bg-white/2.5 ring-1 ring-white/15 transition peer-focus:ring-sky-300" />
		</form>
	);
}
